// React bindings for the stores + a shared 1 Hz clock so relative ages tick live.

import { useEffect, useState } from 'react';
import { authStore, type AuthState } from './authStore';
import { dataStore } from './dataStore';
import { uiStore, type UiState } from './uiStore';
import type { FeedState } from './reducer';
import { useStore } from './store';

export function useData<S>(selector: (s: FeedState) => S): S {
  return useStore(dataStore, selector);
}

export function useUi<S>(selector: (s: UiState) => S): S {
  return useStore(uiStore, selector);
}

export function useAuth<S>(selector: (s: AuthState) => S): S {
  return useStore(authStore, selector);
}

/**
 * A ticking "now" (epoch ms), updated every `intervalMs`. Drives relative-age re-rendering.
 * A single interval per hook instance; components that need ages subscribe once near the top.
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Estimated server-now (epoch ms) = ticking local now + measured clock offset. */
export function useServerNow(intervalMs = 1000): number {
  const now = useNow(intervalMs);
  const offset = useData((s) => s.clockOffsetMs);
  return now + offset;
}

/**
 * Ticking age (seconds) of the last successful trading-VPS poll. 0 while the feed is fresh,
 * before the first poll lands, or while the feed is paused (pause = frozen snapshot, so the
 * trading-VPS band must not silently drift toward OFFLINE). Drives the stale indicator and
 * the client-side status degradation on the band, page, and sidebar badge.
 */
export function useTradingVpsDataAgeS(): number {
  const now = useNow(1000);
  const at = useData((s) => s.tradingVpsAt);
  const paused = useData((s) => s.paused);
  if (paused || at == null) return 0;
  return Math.max(0, (now - at) / 1000);
}
